if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          mediaItems: ['.product__media-item'],
        };

        this.sliderInstance = false;
        this.thumbsInstance = false;

        // EN (language-specific) gallery: variant.featured_media points to the German media, which
        // doesn't exist among the EN slides. This map provides variantId -> synthetic EN slide id.
        this.enImages = false;
        this.variantImageEnLoaded = false;
        this.variantImageEnMap = new Map(); // variantId -> 'en-<index>'
      }

      /** Salespage: kein window.scrollTo / scrollIntoView bei Variantenwechsel (offsetTop ist hier oft falsch → Sprung nach oben). */
      get easeSkipPageScrollOnVariantMedia() {
        return (
          document.body.classList.contains('salespage-v1') ||
          document.body.classList.contains('simple-pdp')
        );
      }

      connectedCallback() {
        this.init();
      }

      init() {
        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';
        this.enImages = this.dataset.enImages === 'true';
        this.setSliderOptions();

        this.loadVariantImageEnMap();

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.updateMediaLayout.bind(this);
        this.updateMediaLayout();

        if (this.enableImageZoom) {
          this.initImageZoom();
        }

        // Initial gallery sort on load (after slider init).
        // WICHTIG: product-info per closest(), nicht document.querySelector – sonst falsche Variante bei mehreren Blöcken.
        // Timeout debouncen, damit schnelle Variantenwechsel nicht mehrere verzögerte Resets stapeln.
        if (this._easeGalleryInitTimeout) {
          clearTimeout(this._easeGalleryInitTimeout);
          this._easeGalleryInitTimeout = null;
        }
        this._easeGalleryInitTimeout = setTimeout(() => {
          this._easeGalleryInitTimeout = null;
          const productInfo = this.closest('product-info');
          if (!productInfo || typeof productInfo.getSelectedVariant !== 'function') return;
          const variant = productInfo.getSelectedVariant(productInfo);
          if (!variant) return;

          if (
            this.dataset.variantImageFirst === 'true' &&
            this.resolveVariantMediaId(variant)
          ) {
            this.sortMediaItems(variant);
          }
        }, 100);
      }

      loadVariantImageEnMap() {
        if (this.variantImageEnLoaded) return;
        this.variantImageEnLoaded = true;

        const sourceId = this.dataset.variantEnMediaSource;
        if (!sourceId) return;

        const el = document.getElementById(sourceId);
        if (!el) return;

        try {
          const parsed = JSON.parse(el.textContent || '[]');
          if (!Array.isArray(parsed)) return;
          parsed.forEach((item) => {
            if (!item || !item.variant_id || !item.media_id) return;
            this.variantImageEnMap.set(String(item.variant_id), item.media_id);
          });
        } catch (e) {
          console.warn('Failed to parse variant EN media JSON', e);
        }
      }

      // Returns the slide id to switch to for a variant: the synthetic EN id in the EN gallery,
      // otherwise the standard variant.featured_media.id. Null when nothing maps.
      resolveVariantMediaId(variant) {
        if (!variant) return null;
        if (this.enImages) {
          return this.variantImageEnMap.get(String(variant.id)) || null;
        }
        return variant.featured_media ? variant.featured_media.id : null;
      }

      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);

        this.sliderOptions = {
          init: false,
          slidesPerView: '1',
          spaceBetween: mediaItemGap,
          loop: true,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: true,
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: 'fraction',
          },
          threshold: 2,
        };

        // Fractional slidesPerView shows a partial next thumb as a scroll affordance.
        this.thumbsOptions = {
          slidesPerView: 5.5,
          breakpoints: {
            461: {
              slidesPerView: 6.5,
            },
          },
          spaceBetween: mediaItemGap,
          loop: false,
          freeMode: true,
          watchSlidesProgress: true,
          threshold: 2,
        };

        switch (this.mediaLayout) {
          case 'vertical-carousel':
            this.thumbsOptions = Object.assign({}, this.thumbsOptions, {
              breakpoints: {
                461: {
                  slidesPerView: 6.5,
                },
                768: {
                  direction: 'vertical',
                  slidesPerView: 6.5,
                },
              },
            });
            break;
          case 'slider-freemode':
            this.sliderOptions = Object.assign({}, this.sliderOptions, {
              slidesPerView: 'auto',
            });
            break;
        }

        if (document.body.classList.contains('simple-pdp')) {
          const isVerticalLayout = this.mediaLayout === 'vertical-carousel';
          this.thumbsOptions = Object.assign({}, this.thumbsOptions, {
            slidesPerView: 4.25,
            breakpoints: {
              461: {
                slidesPerView: 4.5,
              },
              768: isVerticalLayout
                ? { direction: 'vertical', slidesPerView: 6.5 }
                : { direction: 'horizontal', slidesPerView: 5.5 },
            },
          });

          // Fixed-height gallery: autoHeight + loop caused a zoom/jump effect while swiping.
          this.sliderOptions = Object.assign({}, this.sliderOptions, {
            autoHeight: false,
            loop: false,
          });
        }
      }

      updateMediaLayout() {
        if (FoxTheme.config.mqlMobile) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      initSlider() {
        if (typeof this.sliderInstance !== 'object') {
          if ((this.enableDesktopSlider || this.enableMobileThumbnails) && this.elements.thumbnails) {
            this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions);
            this.thumbsInstance.init();

            this.sliderOptions.thumbs = {
              swiper: this.thumbsInstance.slider,
              autoScrollOffset: 2,
            };
          }

          this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, [
            FoxTheme.Swiper.Thumbs,
          ]);
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();

          this.sliderInstance.slider.init();
        }
      }

      destroySlider() {
        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy();
          this.sliderInstance = false;
        }
      }

      initThumbsSlider() {
        if (typeof this.thumbsInstance !== 'object') {
          this.thumbsInstance = new window.FoxTheme.Carousel(this.selectors.thumbnails, this.thumbsOptions);
          this.thumbsInstance.init();
        }
      }

      initImageZoom() {
        let dataSource = [];
        const allMedia = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
        if (allMedia) {
          allMedia.forEach((media) => {
            switch (media.dataset.mediaType) {
              case 'model':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('product-model').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'video':
              case 'external_video':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('video-element').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'image':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  src: media.dataset.src,
                  width: media.dataset.pswpWidth,
                  height: media.dataset.pswpHeight,
                  mediaType: media.dataset.mediaType,
                });
                break;
            }
          });
        }

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource: dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
        });

        this.lightbox.addFilter('thumbEl', (thumbEl, { id }, index) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el;
              }
            }
          }

          return thumbEl;
        });

        this.lightbox.addFilter('placeholderSrc', (placeholderSrc, { data: { id } }) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el.src;
              }
            }
          }

          return placeholderSrc;
        });

        this.lightbox.on('change', () => {
          window.pauseAllMedia(this);
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { currIndex } = this.lightbox.pswp;
            if (this.sliderInstance.slider.realIndex !== currIndex) {
              this.sliderInstance.slider.slideToLoop(currIndex, 100, false);
            }
          }
        });

        this.lightbox.on('pointerDown', (e) => {
          if (this.lightbox.pswp.currSlide.data.mediaType != 'image') {
            e.preventDefault();
          }
        });

        this.lightbox.on('uiRegister', () => {
          if (!this.onlyImage) {
            this.lightbox.pswp.ui.registerElement({
              name: 'next',
              ariaLabel: 'Next slide',
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.next();
              },
            });
            this.lightbox.pswp.ui.registerElement({
              name: 'prev',
              ariaLabel: 'Previous slide',
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.prev();
              },
            });
          }
          this.lightbox.pswp.ui.registerElement({
            name: 'close-zoom',
            ariaLabel: 'Close zoom image',
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: (event, el) => {
              this.lightbox.pswp.close();
            },
          });
        });

        this.lightbox.init();

        FoxTheme.utils.addEventDelegate({
          selector: '.js-photoswipe--zoom',
          context: this,
          handler: (e, media) => {
            if (media.dataset.mediaType === 'image') {
              const index = Number(media.dataset.mediaIndex) || 0;
              this.lightbox.loadAndOpen(index);
            }
          },
        });
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (swiper) => {
          const { slides, activeIndex } = swiper;

          if (slides[activeIndex]) {
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      handleSlideChange() {
        this.sliderInstance.slider.on('realIndexChange', (swiper) => {
          const { slides, activeIndex, thumbs } = swiper;

          if (thumbs.swiper) {
            thumbs.swiper.slideTo(activeIndex);
          }

          if (slides[activeIndex]) {
            this.playActiveMedia(slides[activeIndex]);

            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector('product-model');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      setActiveMedia(variant) {
        if (!variant) return;
        const targetMediaId = this.resolveVariantMediaId(variant);
        // EN gallery: only switch when an EN image is actually mapped for this variant.
        if (this.enImages && !targetMediaId) return;
        if (!this.enImages && (!variant.hasOwnProperty('featured_media') || !variant.featured_media)) return;

        const variantImageFirst = this.dataset.variantImageFirst === 'true';

        if (variantImageFirst) {
          // Always use sortMediaItems when variant image first is enabled
          this.sortMediaItems(variant);
        } else if (this.enImages && this.sliderInstance.slider) {
          // EN images have no reliable featured_media.position -> slide to the mapped slide by id.
          const slides = Array.from(this.elements.mediaItems).filter(
            (m) => !m.classList.contains('swiper-slide-duplicate')
          );
          const idx = slides.findIndex((m) => m.dataset.mediaId == targetMediaId);
          if (idx > -1) this.sliderInstance.slider.slideToLoop(idx);
        } else if (this.sliderInstance.slider) {
          // Original behavior: use slider position
          const slideIndex = variant.featured_media.position || 0;
          this.sliderInstance.slider.slideToLoop(slideIndex - 1);
        } else {
          this.sortMediaItems(variant);
        }
      }

      sortMediaItems(variant) {
        if (!variant) return;
        const targetMediaId = this.resolveVariantMediaId(variant);
        if (!targetMediaId) return;

        const variantImageFirst = this.dataset.variantImageFirst === 'true';
        if (!variantImageFirst) {
          // Original behavior: just move selected variant to first
          let newMedias = Array.from(this.elements.mediaItems).filter((m) => !m.classList.contains('swiper-slide-duplicate'));

          // Reset ordering.
          newMedias.sort(function (a, b) {
            return a.dataset.mediaIndex - b.dataset.mediaIndex;
          });

          newMedias.some((media, index) => {
            if (media.dataset.mediaId == targetMediaId) {
              const [element] = newMedias.splice(index, 1);
              newMedias.unshift(element);
              return true;
            }
          });

          this.elements.mediaList.innerHTML = '';
          newMedias.forEach((media) => {
            this.elements.mediaList.appendChild(media);
          });

          if (!FoxTheme.config.mqlMobile && !this.easeSkipPageScrollOnVariantMedia) {
            const selectedMedia = this.querySelector(`[data-media-id="${targetMediaId}"]`);
            if (selectedMedia) {
              window.scrollTo({ top: selectedMedia.offsetTop, behavior: 'smooth' });
            }
          }
          return;
        }

        // New behavior: selected variant first, then non-variant images, then other variant images
        let newMedias = Array.from(this.elements.mediaItems).filter((m) => !m.classList.contains('swiper-slide-duplicate'));

        // Reset ordering.
        newMedias.sort(function (a, b) {
          return a.dataset.mediaIndex - b.dataset.mediaIndex;
        });

        const selectedVariantMediaId = targetMediaId.toString();
        const selectedVariantId = variant.id.toString();
        
        // Separate media into groups: selected variant, non-variant gallery, other variants
        const selectedVariantMedia = [];
        const nonVariantMedia = [];
        const otherVariantMedia = [];

        newMedias.forEach((media) => {
          const mediaId = media.dataset.mediaId;
          const isVariantMedia = media.dataset.isVariantMedia === 'true';
          const variantIds = media.dataset.variantIds ? media.dataset.variantIds.split(',') : [];

          if (mediaId === selectedVariantMediaId) {
            selectedVariantMedia.push(media);
          } else if (isVariantMedia && variantIds.includes(selectedVariantId)) {
            selectedVariantMedia.push(media);
          } else if (isVariantMedia) {
            otherVariantMedia.push(media);
          } else {
            nonVariantMedia.push(media);
          }
        });

        const sortedMedias = [...selectedVariantMedia, ...nonVariantMedia, ...otherVariantMedia];

        this.elements.mediaList.innerHTML = '';
        sortedMedias.forEach((media) => {
          this.elements.mediaList.appendChild(media);
        });

        // Also sort thumbnails if they exist
        if (this.elements.thumbnails) {
          const thumbnailList = this.elements.thumbnails.querySelector('.swiper-wrapper');
          if (thumbnailList) {
            const thumbnailItems = Array.from(thumbnailList.querySelectorAll('.product__thumbs-item'));
            const sortedThumbnails = [];
            
            // Create a map of media ID to thumbnail
            const thumbnailMap = new Map();
            thumbnailItems.forEach((thumb) => {
              const mediaId = thumb.dataset.target;
              thumbnailMap.set(mediaId, thumb);
            });
            
            // Sort thumbnails in the same order as media
            sortedMedias.forEach((media) => {
              const mediaId = media.dataset.mediaId;
              const thumb = thumbnailMap.get(mediaId);
              if (thumb) {
                sortedThumbnails.push(thumb);
              }
            });
            
            thumbnailList.innerHTML = '';
            sortedThumbnails.forEach((thumb) => {
              thumbnailList.appendChild(thumb);
            });
          }
        }

        // Update slider if it exists - destroy and reinitialize for proper sync
        if (this.sliderInstance && this.sliderInstance.slider) {
          // Store current state
          const wasLoop = this.sliderInstance.slider.params.loop;
          const hadThumbs = !!this.sliderInstance.slider.params.thumbs;
          
          // Destroy existing sliders
          this.destroySlider();
          if (this.thumbsInstance && this.thumbsInstance.slider) {
            this.thumbsInstance.slider.destroy(true, true);
            this.thumbsInstance = false;
          }
          
          // Reinitialize slider
          this.initSlider();
          
          // Ensure we're on the first slide (variant image) after reinit
          setTimeout(() => {
            if (this.sliderInstance && this.sliderInstance.slider) {
              // Set to first slide without animation
              if (wasLoop) {
                this.sliderInstance.slider.slideToLoop(0, 0);
              } else {
                this.sliderInstance.slider.slideTo(0, 0);
              }
              
              // Sync thumbs if they exist
              if (hadThumbs && this.thumbsInstance && this.thumbsInstance.slider) {
                this.thumbsInstance.slider.slideTo(0, 0);
              }
            }
          }, 100);
        } else if (this.elements.mediaList && !this.sliderInstance) {
          // If slider doesn't exist yet, just ensure first item is visible
          const firstMedia = this.elements.mediaList.querySelector('.product__media-item');
          if (firstMedia && !this.easeSkipPageScrollOnVariantMedia) {
            firstMedia.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
          }
        }

        if (!FoxTheme.config.mqlMobile && !this.easeSkipPageScrollOnVariantMedia) {
          const selectedMedia = this.querySelector(`[data-media-id="${selectedVariantMediaId}"]`);
          if (selectedMedia) {
            window.scrollTo({ top: selectedMedia.offsetTop, behavior: 'smooth' });
          }
        }
      }
    }
  );
}
